// Vendors tab of the Settings Console: the vendor master (SPEC.md, Vendor Console) with the vendor dialog.

// REACT IMPORTS
import React, { useCallback, useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

// CUSTOM COMPONENTS
import VendorDialog from "./VendorDialog";
import { SortableHeaderCell, useSortedRows } from "../../Admin/TimeOffConsole/sortableTable";

// API, CONSTANTS
import { getAPICall } from "../../../config/apiCalls";
import { GRAY, GREEN, apiError, fmtDay, pillStyle } from "../itBudgetUtils";

const GETTERS = {
	name: (v) => v.name,
	category: (v) => v.category,
	paid_by: (v) => v.paid_by,
	email_sender: (v) => v.email_sender || "",
	aliases: (v) => Number(v.alias_count),
	rules: (v) => Number(v.rule_count),
};

const VendorsTab = ({ canEdit, lookups }) => {
	const { instance, accounts } = useMsal();
	const [vendors, setVendors] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(null); // vendor id, or "new"

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setVendors((await getAPICall(instance, accounts[0], "/api/it-budget/vendors")).data);
		} catch (err) {
			setError(apiError(err, "Couldn't load vendors."));
		} finally {
			setLoading(false);
		}
	}, [instance, accounts]);

	useEffect(() => {
		load();
	}, [load]);

	const { sorted, sortKey, sortDir, onSort } = useSortedRows(vendors, GETTERS, "name", "asc", ["aliases", "rules"]);
	const header = (key, label, align) => (
		<SortableHeaderCell sortKey={key} activeKey={sortKey} dir={sortDir} onSort={onSort} align={align}>
			{label}
		</SortableHeaderCell>
	);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{canEdit && (
				<div style={{ display: "flex", justifyContent: "flex-end" }}>
					<Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen("new")}>
						New Vendor
					</Button>
				</div>
			)}
			{error && <div className="itBudgetError">{error}</div>}
			{loading ? (
				<div className="itBudgetEmpty">
					<CircularProgress />
				</div>
			) : (
				<div className="itBudgetTableContainer">
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								{header("name", "Vendor")}
								{header("category", "Category")}
								<TableCell>Default Budget Line</TableCell>
								{header("paid_by", "Paid By")}
								<TableCell>Card Update</TableCell>
								<TableCell>Renewal</TableCell>
								{header("email_sender", "Email Sender")}
								<TableCell>File Token</TableCell>
								{header("aliases", "Aliases", "right")}
								{header("rules", "Rules", "right")}
								<TableCell>Active</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{sorted.map((v) => (
								<TableRow key={v.id} className="itBudgetClickableRow" onClick={() => setOpen(v.id)}>
									<TableCell>
										<b>{v.name}</b>
									</TableCell>
									<TableCell>{v.category}</TableCell>
									<TableCell>{v.default_budget_line || "—"}</TableCell>
									<TableCell>{v.paid_by}</TableCell>
									<TableCell>{v.card_update || "—"}</TableCell>
									<TableCell>{v.renewal_date ? fmtDay(v.renewal_date) : "—"}</TableCell>
									<TableCell className="itBudgetMono">{v.email_sender || "—"}</TableCell>
									<TableCell className="itBudgetMono">{v.file_name_token || "—"}</TableCell>
									<TableCell align="right">{v.alias_count}</TableCell>
									<TableCell align="right">{v.rule_count}</TableCell>
									<TableCell>
										<span style={pillStyle(v.active ? GREEN : GRAY)}>{v.active ? "Active" : "Inactive"}</span>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
			<VendorDialog
				open={open !== null}
				vendorId={open === "new" ? null : open}
				canEdit={canEdit}
				lookups={lookups}
				onClose={() => setOpen(null)}
				onSaved={(id) => {
					load();
					if (id) setOpen(id);
				}}
			/>
		</div>
	);
};

export default VendorsTab;
